import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/context/LanguageContext";
import { usePolarCheckout } from "@/hooks/usePolarCheckout";
import { POLAR_PRODUCTS } from "@/config/polar";

interface PricingDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const PricingDialog = ({ open = true, onOpenChange }: PricingDialogProps) => {
  const [selectedPlan, setSelectedPlan] = useState<"annual" | "monthly">("annual");
  const navigate = useNavigate();
  const { t, isRtl, formatNumber } = useTranslation();
  const { startCheckout, loading: checkoutLoading } = usePolarCheckout();

  const handleUpgrade = () => {
    const billingCycle = selectedPlan === "annual" ? "yearly" : "monthly";
    const productId = billingCycle === "yearly" ? POLAR_PRODUCTS.pro_yearly : POLAR_PRODUCTS.pro_monthly;
    startCheckout({ productId, plan: "pro", billingCycle });
  };
  if (!open) return null;

  const handleClose = () => {
    onOpenChange?.(false);
  };

  const handleTeamPlanClick = () => {
    handleClose();
    navigate("/pricing");
  };

  const features = [
    { bold: t("pricing.featureUploadsBold"), rest: t("pricing.featureUploadsRest") },
    { text: t("pricing.featureChats") },
    { text: t("pricing.featureQuiz") },
    { text: t("pricing.featureExams") },
    { bold: t("pricing.featureFilesBold"), rest: t("pricing.featureFilesRest") },
    { bold: t("pricing.featurePlacementBold"), rest: t("pricing.featurePlacementRest") },
  ];

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      {/* Dialog */}
      <div
        className="fixed left-1/2 top-1/2 z-[9999] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto sm:max-w-[500px] border border-border rounded-2xl p-4 sm:p-6 pb-5 sm:pb-4 bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        dir={isRtl ? "rtl" : "ltr"}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className={`absolute ${isRtl ? "left-2" : "right-2"} top-2 p-1.5 sm:p-1 rounded-full hover:bg-transparent hover:text-primary transition-colors opacity-70 hover:opacity-100`}
        >
          <X className="h-5 w-5 sm:h-4 sm:w-4" />
        </button>

        {/* Header */}
        <div className="flex flex-col">
          <header className={`text-xl sm:text-2xl font-medium mb-2 ${isRtl ? "text-right" : "text-left"}`}>
            {t("pricing.headline")}
          </header>

          <div className={`mt-1 text-sm sm:text-base flex flex-wrap items-center text-muted-foreground ${isRtl ? "flex-row-reverse justify-end" : ""}`}>
            {t("pricing.joinOver") && <span>{t("pricing.joinOver")}</span>}
            <span className={`font-semibold text-base sm:text-lg bg-correct/10 text-correct px-1.5 sm:px-1 rounded-sm ${t("pricing.joinOver") ? "mx-1.5" : isRtl ? "ml-1.5" : "mr-1.5"} whitespace-nowrap`} dir="ltr">
              {t("pricing.million")}
            </span>
            <span>{t("pricing.learningSmarter")}</span>
          </div>
        </div>

        {/* Features List */}
        <ul className="mt-5 sm:mt-6 mb-5 sm:mb-6 space-y-2 sm:space-y-2.5 text-xs sm:text-sm">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              <Check className={`w-4 h-4 sm:w-5 sm:h-5 ${isRtl ? "ml-2 order-first" : "mr-2"} text-correct flex-shrink-0 mt-0.5`} />
              <span className={isRtl ? "text-right" : ""}>
                {"bold" in feature ? (
                  <><span className="font-medium">{feature.bold}</span> {feature.rest}</>
                ) : (
                  feature.text
                )}
              </span>
            </li>
          ))}
        </ul>

        {/* Pricing Options */}
        <div className="space-y-2 sm:space-y-2.5">
          {/* Annual Plan */}
          <button
            onClick={() => setSelectedPlan("annual")}
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl flex justify-between items-center border-[1.5px] transition-colors text-xs sm:text-sm ${isRtl ? "flex-row-reverse" : ""} ${
              selectedPlan === "annual"
                ? "border-selected-border bg-selected-subtle hover:bg-selected-subtle/80 text-selected"
                : "border-primary/20 bg-background hover:bg-accent text-primary/60 hover:text-primary/60"
            }`}
          >
            <span className={`font-medium flex items-center gap-1.5 ${isRtl ? "flex-row-reverse" : ""}`}>
              {t("pricing.annual")}
              <span
                className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full border-[1.5px] whitespace-nowrap ${
                  selectedPlan === "annual"
                    ? "text-selected border-selected-border"
                    : "text-primary/60 border-primary/20"
                }`}
              >
                {t("pricing.save40")}
              </span>
            </span>
            <div className={`${isRtl ? "text-right" : "text-left"}`}>
              <div className="space-y-0.5">
                <div
                  className={`font-medium transition-colors text-xs sm:text-sm ${
                    selectedPlan === "annual" ? "text-selected" : "text-primary/60"
                  }`}
                >
                  {t("pricing.freeFor7Days")}
                </div>
                <div
                  className={`text-[10px] sm:text-xs font-medium transition-colors ${
                    selectedPlan === "annual" ? "text-selected/80" : "text-muted-foreground"
                  } ${isRtl ? "text-right" : ""}`}
                >
                  {t("pricing.thenAnnual")}
                </div>
              </div>
            </div>
          </button>

          {/* Monthly Plan */}
          <button
            onClick={() => setSelectedPlan("monthly")}
            className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-2xl flex justify-between items-center border-[1.5px] transition-colors text-xs sm:text-sm ${isRtl ? "flex-row-reverse" : ""} ${
              selectedPlan === "monthly"
                ? "border-selected-border bg-selected-subtle hover:bg-selected-subtle/80 text-selected"
                : "border-primary/20 bg-background hover:bg-accent text-primary/60 hover:text-primary/60 hover:border-primary/20"
            }`}
          >
            <span className="font-medium">{t("pricing.monthly")}</span>
            <div className={`${isRtl ? "text-right" : "text-left"}`}>
              <div className="space-y-0.5">
                <div
                  className={`font-medium transition-colors text-xs sm:text-sm ${
                    selectedPlan === "monthly" ? "text-selected" : "text-primary/60"
                  }`}
                >
                  {t("pricing.freeFor7Days")}
                </div>
                <div
                  className={`text-[10px] sm:text-xs font-medium transition-colors ${
                    selectedPlan === "monthly" ? "text-selected/80" : "text-muted-foreground"
                  } ${isRtl ? "text-right" : ""}`}
                >
                  {t("pricing.thenMonthly")}
                </div>
              </div>
            </div>
          </button>

          {/* CTA Button */}
          <Button
            className="w-full p-5 sm:p-6 rounded-2xl font-semibold text-sm sm:text-base hover:bg-primary/90"
            onClick={handleUpgrade}
            disabled={checkoutLoading}
          >
            {checkoutLoading ? "Processing..." : t("pricing.startTrial")}
          </Button>
        </div>

        {/* Footer Link */}
        <div className="mt-3 sm:mt-4 mb-3 sm:mb-4 text-center text-[10px] sm:text-xs text-primary/80">
          <span>{t("pricing.needTeamPlan")}</span>{" "}
          <button 
            onClick={handleTeamPlanClick}
            className="text-primary underline cursor-pointer hover:text-primary/90"
          >
            {t("pricing.clickHere")}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};
